import React, { useEffect } from 'react';
import styled from 'styled-components';
import gql from 'graphql-tag';
import Loader from 'react-loading';
import { connect } from 'react-redux';
import { useQuery } from '@apollo/react-hooks';
import { themes, Patient, ReduxState, JourneyType } from 'kb-shared';
import { useHistory } from 'react-router-dom';
import Step from './Step';
import { Title } from '../styled_common';
import { YellowButton } from '../../components/Buttons';
import { AbsoluteCentered } from '../../styled';
import { buildJourney } from './journeySteps';

const MY_JOURNEY = gql`
  query myJourney {
    journeyType

    appointments: journeyAppointments {
      id
      startTime
      endTime
      timeZone

      appointmentType {
        id
      }
    }

    results {
      afc: follicleCounts {
        date
      }
      labs {
        date
      }
    }

    patientTodos {
      id
      todoId
      title: name
      linkUrl
      buttonText
      description
      completed
    }

    cyclePlan {
      id
      finished
      expectedDeliveryDate
      deliveryDate
    }

    triggerInstruction {
      id
    }
  }
`;

interface MyJourneyQuery {
  journeyType: JourneyType;
  cyclePlan: {
    id: number;
    finished?: string;
    expectedDeliveryDate?: string;
    deliveryDate?: string;
  };
  [key: string]: any;
}

interface Props {
  patient: Patient;
}

const MyJourney = ({ patient }: Props) => {
  const history = useHistory();
  const name = patient && patient.firstName;
  const title = name ? `${name}'s Journey` : 'My Journey';

  useEffect(() => {
    if (window.analytics) {
      window.analytics.page('Journey');
    }
  }, []);

  const { loading, data } = useQuery<MyJourneyQuery>(MY_JOURNEY);

  if (loading || !data) {
    return (
      <Container>
        <Title>{title}</Title>
        <AbsoluteCentered>
          <Loader type="spin" color={themes.colors.KBYellow} height={150} width={150} />
        </AbsoluteCentered>
      </Container>
    );
  }
  const { appointments, results, patientTodos, journeyType, cyclePlan, triggerInstruction } = data;

  let noJourneyMessage = 'Your journey type is not yet supported.';
  if (!journeyType) {
    noJourneyMessage = 'Your Kindbody journey has not yet begun.';
  }

  const journey = buildJourney({
    appointments,
    cyclePlan,
    journeyType,
    patientTodos,
    results,
    triggerInstruction
  });

  if (!journey) {
    return (
      <Container>
        <Title>{title}</Title>
        <NoJourneyContainer>
          <NoJourneyMessage>{noJourneyMessage}</NoJourneyMessage>
          <ScheduleButton onClick={() => history.push('/booking-quiz')}>
            Schedule an appointment
          </ScheduleButton>
        </NoJourneyContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Title>{title}</Title>
      {journey.steps.map(step => (
        <Step {...step} completed={step.isComplete} key={step.title} journeyType={journeyType} />
      ))}
    </Container>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  patient: state.patient.patient
});

export default connect(mapStateToProps)(MyJourney);

const Container = styled.div`
  ${({ theme }) => theme.grid.pageContainer};
  display: flex;
  flex: 1;
  flex-direction: column;
  margin-top: 100px;
`;

const NoJourneyContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.KBNight};
  padding: 20px;
`;

const NoJourneyMessage = styled.h2`
  ${({ theme }) => theme.typos.sectionHeader};
  font-size: 28px;
  color: white;
  text-align: center;
  margin-bottom: 28px;
`;

const ScheduleButton = styled(YellowButton)`
  margin: 20px auto;
  width: fit-content;
`;
